<template>
  <button
    :type="type"
    class="app-button"
    :class="[
      `app-button--${variant}`,
      `app-button--${size}`,
      { 'app-button--block': block, 'app-button--loading': loading, 'app-button--icon-only': iconOnly },
    ]"
    :disabled="disabled || loading"
    :aria-busy="loading ? 'true' : undefined"
  >
    <span v-if="loading" class="cb-spinner app-button__spinner" aria-hidden="true"></span>
    <template v-else>
      <slot name="icon">
        <AppIcon v-if="icon" :name="icon" :size="iconSize" class="app-button__icon" />
      </slot>
    </template>
    <span v-if="$slots.default" class="app-button__label"><slot /></span>
    <AppIcon v-if="iconRight && !loading" :name="iconRight" :size="iconSize" class="app-button__icon" />
  </button>
</template>

<script setup>
/**
 * AppButton — кнопка дизайн-системы.
 *
 * <AppButton icon="play" block @click="play">Игра</AppButton>              // primary lg (экран 1)
 * <AppButton variant="secondary" icon="info" block>Правила</AppButton>
 * <AppButton variant="outline" size="sm">Сменить</AppButton>               // экран 10
 * <AppButton variant="accent" size="sm">Ссылка на приглашение</AppButton>  // лаймовая обводка + свечение
 * <AppButton variant="ghost" size="sm">Отмена</AppButton>
 * <AppButton :loading="saving">Сохранить</AppButton>
 *
 * Варианты: primary | secondary | outline | accent | ghost. Размеры: lg (64px) | md (52px) | sm (40px).
 * Слоты: default (текст), icon (своя иконка слева). Нативные события (@click) проходят на <button>.
 */
import { computed } from "vue";
import AppIcon from "./AppIcon.vue";

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (v) => ["primary", "secondary", "outline", "accent", "ghost"].includes(v),
  },
  size: { type: String, default: "lg", validator: (v) => ["lg", "md", "sm"].includes(v) },
  /** Имя иконки AppIcon слева от текста. */
  icon: { type: String, default: "" },
  /** Имя иконки AppIcon справа от текста. */
  iconRight: { type: String, default: "" },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  /** Растянуть на всю ширину. */
  block: { type: Boolean, default: false },
  /** Квадратная кнопка только с иконкой (aria-label передавайте атрибутом). */
  iconOnly: { type: Boolean, default: false },
  type: { type: String, default: "button" },
});

const iconSize = computed(() => ({ lg: 26, md: 22, sm: 18 })[props.size]);
</script>

<style scoped>
.app-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  margin: 0;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: var(--cb-font);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition:
    transform var(--cb-dur-fast) var(--cb-ease),
    background-color var(--cb-dur) var(--cb-ease),
    box-shadow var(--cb-dur) var(--cb-ease),
    border-color var(--cb-dur) var(--cb-ease),
    opacity var(--cb-dur) var(--cb-ease);
}

.app-button:active:not(:disabled) {
  transform: scale(0.98);
}

.app-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.app-button--loading:disabled {
  opacity: 0.85;
}

.app-button--block {
  display: flex;
  width: 100%;
}

/* Inline-кнопки не сжимаются в рядах (кроме sm outline/accent, у которых текст переносится). */
.app-button:not(.app-button--block):not(.app-button--sm.app-button--outline):not(.app-button--sm.app-button--accent) {
  flex-shrink: 0;
}

.app-button__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* sizes */
.app-button--lg {
  height: 64px;
  padding: 0 28px;
  border-radius: var(--cb-radius-lg);
  font-size: 22px;
  font-weight: 800;
}

.app-button--md {
  height: 52px;
  padding: 0 22px;
  border-radius: var(--cb-radius-md);
  font-size: 17px;
}

.app-button--sm {
  height: 40px;
  padding: 0 16px;
  border-radius: var(--cb-radius-sm);
  font-size: 14px;
  gap: 8px;
}

.app-button--icon-only {
  padding: 0;
  aspect-ratio: 1 / 1;
}

/* variants */
.app-button--primary {
  background: var(--cb-lime);
  color: var(--cb-on-lime);
  box-shadow: var(--cb-glow), inset 0 1px 0 rgba(255, 255, 255, 0.45), inset 0 -2px 0 rgba(0, 0, 0, 0.08);
}

.app-button--primary:hover:not(:disabled) {
  box-shadow: var(--cb-glow-lg), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.app-button--primary:active:not(:disabled) {
  background: var(--cb-lime-press);
}

.app-button--secondary {
  background: linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  border-color: var(--cb-border-strong);
  color: var(--cb-text);
  box-shadow: var(--cb-inner-highlight);
}

.app-button--secondary:hover:not(:disabled),
.app-button--secondary:active:not(:disabled) {
  background: var(--cb-surface-3);
}

.app-button--outline {
  background: rgba(255, 255, 255, 0.02);
  border-color: var(--cb-border-strong);
  color: var(--cb-lime);
  box-shadow: var(--cb-inner-highlight);
}

.app-button--outline:hover:not(:disabled),
.app-button--outline:active:not(:disabled) {
  border-color: var(--cb-lime-border);
}

.app-button--accent {
  background: var(--cb-lime-softer);
  border-color: var(--cb-lime);
  color: var(--cb-lime);
  box-shadow: var(--cb-glow-sm), inset 0 0 12px rgba(221, 245, 90, 0.08);
}

.app-button--accent:active:not(:disabled) {
  background: var(--cb-lime-soft);
}

.app-button--ghost {
  background: transparent;
  color: var(--cb-text-2);
}

.app-button--ghost:hover:not(:disabled),
.app-button--ghost:active:not(:disabled) {
  color: var(--cb-text);
  background: rgba(255, 255, 255, 0.04);
}

.app-button--sm.app-button--accent,
.app-button--sm.app-button--outline {
  white-space: normal;
  line-height: 1.15;
  text-align: center;
}

.app-button__spinner {
  font-size: 1.05em;
}

.app-button--primary .app-button__spinner {
  border-color: rgba(11, 14, 10, 0.2);
  border-top-color: var(--cb-on-lime);
}
</style>
